import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ExternalLink, FileText, Pencil, Plus, RotateCcw, Search, Trash2, Upload, X } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty";
import { Badge } from "@/components/ui/badge";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { AdvertiserOwnerBadge } from "@/components/reassign-advertiser-popover";
import { discardDraft, listDrafts, purgeDraft, renameDraft, restoreDraft } from "@/api/constructor";
import type { DraftDto } from "@/types/constructor";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import { AdvertiserSelect } from "@/components/advertiser-select";
import { useAdvertiserLookup } from "@/components/advertiser-cell";
import { ownerFilterFromUrl, ownerFilterParam, ownerFilterToUrl } from "@/api/commerce";

type Tab = "active" | "published" | "discarded" | "all";

const TAB_LABELS: Record<Tab, string> = {
    active: "Active",
    published: "Published",
    discarded: "Discarded",
    all: "All",
};

export function DraftsListPage(): React.ReactNode {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [tab, setTab] = React.useState<Tab>("active");
    const [searchInput, setSearchInput] = React.useState("");
    const [search, setSearch] = React.useState("");
    React.useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 200);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Advertiser filter — URL-persisted via ?advertiserId= for shareable links.
    const [searchParams, setSearchParams] = useSearchParams();
    const advertiserId = ownerFilterFromUrl(searchParams.get("advertiserId"));
    const setAdvertiserId = React.useCallback(
        (next: number | null | undefined): void => {
            setSearchParams(
                (p) => {
                    const v = ownerFilterToUrl(next);
                    if (v == null) p.delete("advertiserId");
                    else p.set("advertiserId", v);
                    return p;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );
    const advLookup = useAdvertiserLookup();
    const showAdvertiserFilter = advLookup.advertisers.length > 0;

    // Status filtering happens server-side (correct pagination); "all" omits the param.
    const draftsQuery = useQuery({
        queryKey: ["drafts", advertiserId, tab],
        queryFn: () => listDrafts({
            advertiserId: ownerFilterParam(advertiserId),
            status: tab === "all" ? undefined : tab,
        }),
    });
    const discardMut = useMutation({
        mutationFn: (id: number) => discardDraft(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            toast.success("Draft discarded");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Discard failed")),
    });
    const restoreMut = useMutation({
        mutationFn: (id: number) => restoreDraft(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            toast.success("Draft restored");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Restore failed")),
    });
    const purgeMut = useMutation({
        mutationFn: (id: number) => purgeDraft(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            toast.success("Draft permanently deleted");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Purge failed")),
    });

    const allDrafts: DraftDto[] = draftsQuery.data || [];
    const filtered = allDrafts
        .filter((d) => !search || d.name.toLowerCase().includes(search) || (d.ownerEmail ?? "").toLowerCase().includes(search));

    return (
        <>
            <PageHeader
                title="Constructor"
                description="In-progress AI work. Each draft becomes a published Page version when you ship it. Open one to resume the chat, or start fresh."
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => navigate("/pages/new?mode=ai-upload")}>
                            <Upload className="h-4 w-4" />
                            Upload bundle
                        </Button>
                        <Button onClick={() => navigate("/pages/new?mode=ai")} data-testid="new-draft-button">
                            <Plus className="h-4 w-4" />
                            New draft
                        </Button>
                    </div>
                }
            />
            <div className="px-6 py-4 space-y-3">
                {/* Tabs + search */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="inline-flex rounded-md border border-border bg-card p-0.5">
                        {(["active", "published", "discarded", "all"] as const).map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTab(t)}
                                className={cn(
                                    "rounded px-3 py-1.5 text-sm transition-colors",
                                    tab === t ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
                                )}
                                data-testid={`drafts-tab-${t}`}
                            >
                                {TAB_LABELS[t]}
                                {/* Filtering is server-side now, so only the loaded tab's count is known. */}
                                {tab === t && !draftsQuery.isLoading && (
                                    <span className="ml-1.5 text-[10px] text-muted-foreground">{allDrafts.length}</span>
                                )}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 min-w-[260px]">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by name or owner email…"
                            className="h-7 border-0 bg-transparent px-0 focus-visible:ring-0"
                            data-testid="drafts-search"
                        />
                    </div>
                    {showAdvertiserFilter && (
                        <div className="w-[200px]">
                            <AdvertiserSelect
                                value={advertiserId}
                                onChange={setAdvertiserId}
                                data-testid="drafts-advertiser-filter"
                            />
                        </div>
                    )}
                </div>

                {draftsQuery.isLoading && (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                )}
                {!draftsQuery.isLoading && filtered.length === 0 && (
                    <EmptyState
                        icon={<FileText className="h-8 w-8" />}
                        title={search
                            ? `No drafts match "${searchInput}"`
                            : tab === "published" ? "No published drafts yet"
                            : tab === "active" ? "No active drafts"
                            : tab === "discarded" ? "No discarded drafts"
                            : "No drafts"}
                        description={search
                            ? "Try a different search term."
                            : tab === "discarded"
                                ? "Drafts you discard land here, where you can restore or permanently delete them."
                                : "Start a fresh draft with the AI, or upload a bundle to adapt."}
                        action={!search && tab !== "discarded" ? (
                            <div className="flex gap-2">
                                <Button onClick={() => navigate("/pages/new?mode=ai")}>
                                    <Plus className="h-4 w-4" /> Start with AI
                                </Button>
                                <Button variant="outline" onClick={() => navigate("/pages/new?mode=ai-upload")}>
                                    <Upload className="h-4 w-4" /> Upload bundle
                                </Button>
                            </div>
                        ) : undefined}
                    />
                )}
                {filtered.length > 0 && (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {filtered.map((d) => (
                            <DraftCard
                                key={d.id}
                                draft={d}
                                onOpen={() => navigate(`/constructor/draft/${d.id}`)}
                                onDiscard={() => confirm.show({
                                    title: `Discard "${d.name}"?`,
                                    description: "Discards this draft. It moves to the Discarded tab where you can restore or permanently delete it.",
                                    destructive: true,
                                    confirmLabel: "Discard",
                                    onConfirm: async () => {
                                        await new Promise<void>((resolve, reject) => {
                                            discardMut.mutate(d.id, { onSuccess: () => resolve(), onError: (e) => reject(e) });
                                        });
                                    },
                                })}
                                onRestore={() => restoreMut.mutate(d.id)}
                                restorePending={restoreMut.isPending}
                                onPurge={() => confirm.show({
                                    title: `Purge "${d.name}"?`,
                                    description: "Permanently delete this draft and its files? This can't be undone.",
                                    destructive: true,
                                    confirmLabel: "Delete permanently",
                                    onConfirm: async () => {
                                        await new Promise<void>((resolve, reject) => {
                                            purgeMut.mutate(d.id, { onSuccess: () => resolve(), onError: (e) => reject(e) });
                                        });
                                    },
                                })}
                            />
                        ))}
                    </div>
                )}
            </div>
            {confirm.dialog}
        </>
    );
}

function shortOwner(email: string): string {
    const at = email.indexOf("@");
    return at > 0 ? email.slice(0, at) : email;
}

function DraftCard({
    draft,
    onOpen,
    onDiscard,
    onRestore,
    restorePending = false,
    onPurge,
}: {
    draft: DraftDto;
    onOpen: () => void;
    onDiscard: () => void;
    onRestore: () => void;
    restorePending?: boolean;
    onPurge: () => void;
}): React.ReactNode {
    const queryClient = useQueryClient();
    const isPublished = draft.status === "published";
    const isDiscarded = draft.status === "discarded";
    const [editing, setEditing] = React.useState(false);
    const [draftName, setDraftName] = React.useState(draft.name);
    React.useEffect(() => { if (!editing) setDraftName(draft.name); }, [draft.name, editing]);

    const renameMut = useMutation({
        mutationFn: (newName: string) => renameDraft(draft.id, newName),
        onSuccess: (d) => {
            toast.success(`Renamed to "${d.name}"`);
            setEditing(false);
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
        },
        onError: (e) => toast.error(apiErrorMessage(e, "Couldn't rename draft")),
    });

    const commit = () => {
        const v = draftName.trim();
        if (!v) { setEditing(false); setDraftName(draft.name); return; }
        if (v === draft.name) { setEditing(false); return; }
        renameMut.mutate(v);
    };

    return (
        <div
            data-testid={`draft-card-${draft.id}`}
            className="group relative flex flex-col gap-2 rounded-lg border border-border bg-card p-4 transition-colors hover:bg-muted/30"
        >
            {editing ? (
                <form
                    className="flex items-center gap-1.5"
                    onSubmit={(e) => { e.preventDefault(); commit(); }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <Input
                        autoFocus
                        className="h-7 text-sm"
                        value={draftName}
                        maxLength={160}
                        onChange={(e) => setDraftName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") { setEditing(false); setDraftName(draft.name); }
                        }}
                        data-testid={`rename-input-${draft.id}`}
                    />
                    <Button type="submit" size="sm" variant="ghost" disabled={renameMut.isPending} data-testid={`rename-save-${draft.id}`}>
                        <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setEditing(false); setDraftName(draft.name); }}>
                        <X className="h-3.5 w-3.5" />
                    </Button>
                </form>
            ) : (
                <button
                    type="button"
                    aria-label={`Open ${draft.name}`}
                    onClick={onOpen}
                    className="flex flex-1 flex-col items-start gap-1.5 text-left"
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{draft.name}</span>
                        {isPublished && <Badge variant="secondary">published</Badge>}
                        {isDiscarded && <Badge variant="muted">discarded</Badge>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{draft.fileCount} file{draft.fileCount === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span>{draft.assetCount} asset{draft.assetCount === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span>{Math.round(draft.totalSizeBytes / 1024)} KB</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                        edited {formatRelativeTime(draft.updatedAt)}
                        {draft.ownerEmail && (
                            <>
                                {" "}· by <span title={draft.ownerEmail}>{shortOwner(draft.ownerEmail)}</span>
                            </>
                        )}
                    </div>
                </button>
            )}
            {!editing && (
                <div
                    className="flex flex-wrap items-center gap-2"
                    // Stop propagation so clicking the badge / pencil doesn't bubble
                    // up to the surrounding "open draft" button.
                    onClick={(e) => e.stopPropagation()}
                >
                    <AdvertiserOwnerBadge
                        entityType="page_draft"
                        entityId={draft.id}
                        currentAdvertiserId={draft.advertiserId ?? null}
                        compact
                        data-testid={`draft-card-advertiser-badge-${draft.id}`}
                    />
                </div>
            )}
            {isPublished && draft.pageId && !editing && (
                <Link
                    to={`/pages/${draft.pageId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    data-testid={`draft-view-page-${draft.id}`}
                >
                    <ExternalLink className="h-3 w-3" /> View page
                </Link>
            )}
            {isDiscarded && !editing && (
                <div
                    className="flex flex-wrap items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onRestore}
                        disabled={restorePending}
                        title="Move this draft back to Active"
                        data-testid={`restore-${draft.id}`}
                    >
                        {restorePending ? <Spinner size="sm" /> : <RotateCcw className="h-3.5 w-3.5" />}
                        Restore
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={onPurge}
                        className="text-muted-foreground hover:text-destructive"
                        title="Permanently delete this draft and its files"
                        data-testid={`purge-${draft.id}`}
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete permanently
                    </Button>
                </div>
            )}
            {!editing && !isDiscarded && (
                <div className="absolute right-2 top-2 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <button
                        type="button"
                        aria-label={`Rename ${draft.name}`}
                        title="Rename draft"
                        onClick={() => setEditing(true)}
                        className="rounded p-1 hover:bg-muted"
                        data-testid={`rename-${draft.id}`}
                    >
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                    </button>
                    <button
                        type="button"
                        aria-label={`Discard ${draft.name}`}
                        title="Discard draft"
                        onClick={onDiscard}
                        className="rounded p-1 hover:bg-muted"
                        data-testid={`discard-${draft.id}`}
                    >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                    </button>
                </div>
            )}
        </div>
    );
}
